import React, { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Select,
  MenuItem,
  Breadcrumbs,
  Link as MuiLink,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Chip,
  Paper,
  Button,
  InputAdornment,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import FolderRoundedIcon from "@mui/icons-material/FolderRounded";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import StorageRoundedIcon from "@mui/icons-material/StorageRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import NorthRoundedIcon from "@mui/icons-material/NorthRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DriveFolderUploadRoundedIcon from "@mui/icons-material/DriveFolderUploadRounded";
import { useQuery } from "@tanstack/react-query";
import { listStorages, listObjects, ArtifactItem } from "../../../../../api/storage/storage";

export type StorageSelection =
  | { type: "storage"; bucket: string; path: string; name: string }
  | { type: "url"; url: string }
  | null;

type Badge = { label: string; color: string };

type Props = {
  allowSelect: (item: ArtifactItem) => boolean;
  onChange: (sel: StorageSelection) => void;
  /** Optional right-side badge for a row (e.g. resource / .zip). */
  badgeFor?: (item: ArtifactItem) => Badge | undefined;
  enableUrl?: boolean;
  enableFolderSelect?: boolean;
  urlPattern?: RegExp;
  urlPlaceholder?: string;
  urlHint?: string;
};

const StorageBrowser: React.FC<Props> = ({
  allowSelect,
  onChange,
  badgeFor,
  enableUrl = true,
  enableFolderSelect = false,
  urlPattern,
  urlPlaceholder = "https://host/bucket/file.zip",
  urlHint = "Direct public URL",
}) => {
  const [mode, setMode] = useState<"storage" | "url">("storage");
  const [selectedBucket, setSelectedBucket] = useState("");
  const [prefix, setPrefix] = useState("");
  const [selectedPath, setSelectedPath] = useState<string>("");
  const [url, setUrl] = useState("");

  const { data: buckets = [], isLoading: loadingBuckets } = useQuery({
    queryKey: ["buckets"],
    queryFn: listStorages,
  });
  const { data: files = [], isLoading: loadingFiles } = useQuery({
    queryKey: ["files", selectedBucket, prefix],
    queryFn: () => (selectedBucket ? listObjects(selectedBucket, prefix) : Promise.resolve([])),
    enabled: !!selectedBucket,
  });

  const prefixParts = prefix ? prefix.split("/").filter(Boolean) : [];
  const crumbs = [
    { label: selectedBucket || "bucket", prefix: "" },
    ...prefixParts.map((p, idx) => ({ label: p, prefix: prefixParts.slice(0, idx + 1).join("/") + "/" })),
  ];

  const pickStorage = (bucket: string, path: string, name: string) => {
    setSelectedPath(path);
    onChange({ type: "storage", bucket, path, name });
  };

  const openFolder = (path: string) => {
    setPrefix(path.endsWith("/") ? path : path + "/");
    setSelectedPath("");
    onChange(null);
  };

  const goUp = () => {
    const up = prefixParts.slice(0, -1).join("/");
    setPrefix(up ? up + "/" : "");
    setSelectedPath("");
    onChange(null);
  };

  const handleUrl = (v: string) => {
    setUrl(v);
    const ok = v.trim() && (!urlPattern || urlPattern.test(v.trim()));
    onChange(ok ? { type: "url", url: v.trim() } : null);
  };

  const urlInvalid = !!url && !!urlPattern && !urlPattern.test(url.trim());

  return (
    <Box>
      {enableUrl && (
        <ToggleButtonGroup
          value={mode}
          exclusive
          size="small"
          onChange={(_, m) => {
            if (m) {
              setMode(m);
              setSelectedPath("");
              setUrl("");
              onChange(null);
            }
          }}
          sx={{
            mb: 2,
            "& .MuiToggleButton-root": { textTransform: "none", px: 2, py: 0.6, fontWeight: 600 },
          }}
        >
          <ToggleButton value="storage">
            <StorageRoundedIcon sx={{ mr: 1, fontSize: 18 }} /> Storage
          </ToggleButton>
          <ToggleButton value="url">
            <LinkRoundedIcon sx={{ mr: 1, fontSize: 18 }} /> URL
          </ToggleButton>
        </ToggleButtonGroup>
      )}

      {enableUrl && mode === "url" && (
        <TextField
          fullWidth
          size="small"
          label={urlHint}
          placeholder={urlPlaceholder}
          value={url}
          onChange={(e) => handleUrl(e.target.value)}
          error={urlInvalid}
          helperText={urlInvalid ? "Enter a valid direct file URL" : " "}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LinkRoundedIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />
      )}

      {(!enableUrl || mode === "storage") && (
        <Stack spacing={1.5}>
          {loadingBuckets ? (
            <CircularProgress size={22} />
          ) : (
            <Select
              value={selectedBucket}
              onChange={(e) => {
                setSelectedBucket(e.target.value);
                setPrefix("");
                setSelectedPath("");
                onChange(null);
              }}
              displayEmpty
              size="small"
              fullWidth
              startAdornment={
                <InputAdornment position="start">
                  <StorageRoundedIcon fontSize="small" color="action" />
                </InputAdornment>
              }
            >
              <MenuItem value="" disabled>
                Select a storage bucket…
              </MenuItem>
              {buckets.map((b) => (
                <MenuItem key={b} value={b}>
                  {b}
                </MenuItem>
              ))}
            </Select>
          )}

          {selectedBucket && (
            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
              {/* path bar */}
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ px: 1.5, py: 1, bgcolor: "background.level1", borderBottom: "1px solid", borderColor: "divider" }}
              >
                <Breadcrumbs sx={{ fontSize: 13 }}>
                  {crumbs.map((c, i) =>
                    i < crumbs.length - 1 ? (
                      <MuiLink
                        key={c.prefix}
                        underline="hover"
                        color="primary"
                        sx={{ cursor: "pointer", fontWeight: 600 }}
                        onClick={() => {
                          setPrefix(c.prefix);
                          setSelectedPath("");
                          onChange(null);
                        }}
                      >
                        {c.label}
                      </MuiLink>
                    ) : (
                      <Typography key={c.prefix} color="text.primary" sx={{ fontWeight: 700, fontSize: 13 }}>
                        {c.label}
                      </Typography>
                    )
                  )}
                </Breadcrumbs>
                {enableFolderSelect && prefix && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<DriveFolderUploadRoundedIcon />}
                    onClick={() =>
                      pickStorage(selectedBucket, prefix, prefixParts.slice(-1)[0] || prefix)
                    }
                    sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                  >
                    Use this folder
                  </Button>
                )}
              </Stack>

              {/* rows */}
              <Box sx={{ maxHeight: 340, overflow: "auto" }}>
                {loadingFiles ? (
                  <Box sx={{ p: 3, textAlign: "center" }}>
                    <CircularProgress size={22} />
                  </Box>
                ) : (
                  <>
                    {prefix && <Row icon={<NorthRoundedIcon fontSize="small" />} name=".." muted onClick={goUp} />}
                    {files.length === 0 && (
                      <Typography sx={{ p: 2, color: "text.secondary", fontSize: 13 }}>
                        This folder is empty.
                      </Typography>
                    )}
                    {files.map((item) => {
                      const selectable = allowSelect(item);
                      const isFolder = item.type === "folder";
                      const isSelected = selectedPath === item.path;
                      const badge = badgeFor?.(item);
                      return (
                        <Row
                          key={item.path}
                          icon={
                            isFolder ? (
                              <FolderRoundedIcon fontSize="small" sx={{ color: "#f4b740" }} />
                            ) : (
                              <InsertDriveFileOutlinedIcon fontSize="small" sx={{ color: selectable ? "text.secondary" : "text.disabled" }} />
                            )
                          }
                          name={item.name}
                          badge={badge}
                          selected={isSelected}
                          disabled={!isFolder && !selectable}
                          onClick={() => {
                            if (isFolder) openFolder(item.path);
                            else if (selectable) pickStorage(selectedBucket, item.path, item.name);
                          }}
                        />
                      );
                    })}
                  </>
                )}
              </Box>
            </Paper>
          )}
        </Stack>
      )}
    </Box>
  );
};

// A single file/folder row.
const Row = ({
  icon,
  name,
  badge,
  selected,
  disabled,
  muted,
  onClick,
}: {
  icon: React.ReactNode;
  name: string;
  badge?: Badge;
  selected?: boolean;
  disabled?: boolean;
  muted?: boolean;
  onClick?: () => void;
}) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1.25}
    onClick={disabled ? undefined : onClick}
    sx={{
      px: 1.5,
      py: 1,
      borderBottom: "1px solid",
      borderColor: "divider",
      "&:last-of-type": { borderBottom: 0 },
      cursor: disabled ? "default" : "pointer",
      color: muted || disabled ? "text.disabled" : "text.primary",
      bgcolor: (t) => (selected ? alpha(t.palette.primary.main, 0.1) : "transparent"),
      boxShadow: (t) => (selected ? `inset 3px 0 0 ${t.palette.primary.main}` : "none"),
      "&:hover": { bgcolor: (t) => (disabled ? "transparent" : alpha(t.palette.primary.main, selected ? 0.14 : 0.05)) },
    }}
  >
    {icon}
    <Typography sx={{ flexGrow: 1, fontSize: 13.5, fontWeight: selected ? 700 : 500, wordBreak: "break-all" }}>
      {name}
    </Typography>
    {badge && (
      <Chip
        size="small"
        label={badge.label}
        sx={{
          height: 20,
          fontSize: "0.66rem",
          fontWeight: 700,
          color: badge.color,
          bgcolor: alpha(badge.color, 0.12),
          border: `1px solid ${alpha(badge.color, 0.32)}`,
        }}
      />
    )}
    {selected && <CheckCircleRoundedIcon fontSize="small" color="primary" />}
  </Stack>
);

export default StorageBrowser;
